import React, { useState, useEffect, useContext, useRef, useMemo } from "react";
import "./Restaurant.css";
import { Context } from "../../context/Context";
import { useAccess } from "../../access/hooks/useAccess";
import { RestaurantRow } from "./RestaurantRow";
import { exportRestaurantsToExcel } from "../../utils/exportExcel";
import { translations } from "../../utils/lang";
import { fetchStoresAPI } from "../../utils/api/stores";
import { notifyError } from "../../utils/notify";
import { Button, Input } from "../ui";
import { CloudDownload } from "../ui/icons";

const STORES_LOAD_FAILED = {
  en: "Could not load the restaurants. Check your connection and reload.",
  ar: "تعذّر تحميل المطاعم. تحقق من اتصالك وأعد تحميل الصفحة.",
};

export const Restaurant = () => {
  const {
    lang,
    storeAccess,
    handleTokenExpiration,
  } = useContext(Context);
  const { can } = useAccess();

  const [savedData, setSavedData] = useState([]);
  const [filterValues, setFilterValues] = useState(
    Array(translations[lang].restaurants.header.length).fill("")
  );

  const restStatus = [
    "new",
    "pending",
    "active",
    "closed",
    "inProgress",
    "startSubscription",
  ];
  const [statusFilters, setStatusFilters] = useState([]);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);

  const StatusDropdownRef = useRef(null);

  const fetchStores = async () => {
    const token = localStorage.getItem("auth-token");

    if (token) {
      try {
        // Strict: a rejected token ends the session, a network or server
        // problem does not (NA-036) and is reported without a dialog.
        const stores = await fetchStoresAPI(token, storeAccess, { strict: true });
        setSavedData(stores);
      } catch (error) {
        if (error?.kind === "rejected") handleTokenExpiration();
        else notifyError(STORES_LOAD_FAILED, { key: "restaurants-load-failed" });
      }
    } else {
      handleTokenExpiration();
    }
  };

  useEffect(() => {
    fetchStores();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        StatusDropdownRef.current &&
        !StatusDropdownRef.current.contains(e.target)
      ) {
        setShowStatusDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const ExportToExcel = () => {
    // Checked here, not only on the button: a denied export builds no file (NA-010).
    if (!can("restaurant.export")) return;
    exportRestaurantsToExcel(savedData);
  };

  const handleFilterChange = (index, value) => {
    if (index === 7) return;

    const newFilters = [...filterValues];
    newFilters[index] = value.toLowerCase();
    setFilterValues(newFilters);
  };

  const filteredData = useMemo(
    () =>
      savedData.filter((item) => {
        const valuesToCheck = [
          new Date(item.date).toLocaleDateString() || "",
          item.id || "",
          item.businessName || "",
          item.branches?.length?.toString() || "",
          item.name || "",
          item.phone || "",
          item.city || "",
        ];

        const baseFiltersMatch = filterValues.every((filter, i) => {
          if (!filter || i === 7) return true;
          return valuesToCheck[i]?.toLowerCase().includes(filter);
        });

        const statusMatch =
          statusFilters.length === 0 || statusFilters.includes(item.status);

        return baseFiltersMatch && statusMatch;
      }),
    [savedData, filterValues, statusFilters]
  );

  return (
    <div className="restaurants-container">
      {can("restaurant.export") && (
        <div className="exportToExcel">
          <Button
            className={`export-btn ${lang === "ar" ? "ar" : ""}`}
            onClick={ExportToExcel}
            variant="primary"
            appearance="soft"
            size="sm"
            rounded
            icon={<CloudDownload className="exportToExcel-img" size={16} />}
            iconPosition="end"
          >
            export
          </Button>
        </div>
      )}

      <div className="table-scroll-container">
        <table className="restaurants-list-table-container">
          <thead>
            <tr>
              {translations[lang].restaurants.header.map((item, i) => {
                return (
                  <th className="restaurants-list-table-header" key={i}>
                    <p>{item}</p>

                    {i < 7 ? (
                      <Input
                        type="text"
                        value={filterValues[i]}
                        onChange={(e) =>
                          handleFilterChange(i, e.target.value)
                        }
                        placeholder={lang === "ar" ? "بحث" : "search"}
                      />
                    ) : i === 7 && can("restaurant.status.filter") ? (
                      <div
                        className="status-filter-dropdown"
                        ref={StatusDropdownRef}
                      >
                        <Button
                          type="button"
                          className="status-filter-btn"
                          onClick={() =>
                            setShowStatusDropdown((prev) => !prev)
                          }
                          variant="neutral"
                          appearance=""
                          size="sm"
                        >
                          {translations[lang].restaurants.selectStatus}
                        </Button>

                        {showStatusDropdown && (
                          <div className="status-dropdown-menu">
                            {restStatus.map((status, idx) => (
                              <label
                                key={idx}
                                className="status-option status-ios-checkbox"
                              >
                                <input
                                  type="checkbox"
                                  checked={statusFilters.includes(status)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setStatusFilters((prev) => [
                                        ...prev,
                                        status,
                                      ]);
                                    } else {
                                      setStatusFilters((prev) =>
                                        prev.filter((s) => s !== status)
                                      );
                                    }
                                  }}
                                />

                                <div className="status-checkbox-wrapper">
                                  <div className="status-checkbox-bg"></div>

                                  <svg
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    className="status-checkbox-icon"
                                  >
                                    <path
                                      strokeLinejoin="round"
                                      strokeLinecap="round"
                                      strokeWidth="3"
                                      stroke="currentColor"
                                      d="M4 12L10 18L20 6"
                                      className="status-check-path"
                                    ></path>
                                  </svg>
                                </div>

                                {status === "startSubscription"
                                  ? lang === "ar"
                                    ? "بدء الاشتراك"
                                    : "Start Subscription"
                                  : translations[lang].restaurants
                                      .restaurantsRow.status[status]}
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : null}
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody>
            {filteredData.length === 0 ? (
              <tr>
                <td
                  className="restaurants-list-table-p"
                  colSpan={translations[lang].restaurants.header.length}
                >
                  {lang === "ar" ? "لا توجد بيانات" : "No data found."}
                </td>
              </tr>
            ) : (
              filteredData.map((item, index) => (
                <RestaurantRow
                  key={index}
                  item={item}
                  fetchStores={fetchStores}
                />
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};